import {
  AnyObject,
  arrayToObject,
  Collection,
  CollectionId,
  CollectionItem,
  getCollectionId,
  LocalCollectionState,
} from '@hb/shared'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { subscribeToCollection } from '../../../collections/utils/read'
import { useFetchData } from '../../../contexts/db/FetchDataContext'
import { PopUpMessageContext } from '../../../contexts/PopUpMessage'
import { useAuth } from '../../../store/auth'
import { useCollections } from '../../../store/collections'

export const useCollection = <Data extends AnyObject>(
  collection: Collection<Data> | undefined | null,
  paused?: boolean,
): LocalCollectionState<Data> => {
  const fetchData = useFetchData()
  const { showError } = useContext(PopUpMessageContext)
  const [items, setItems] = useState<CollectionItem<Data>[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const accessLevel = useAuth(s => s.accessLevel)
  const collectionId = useMemo(
    () => (collection ? getCollectionId(collection) : null),
    [collection],
  )

  const subscribedCollectionId = useRef<string | null>(null)
  const unsub = useRef<(() => void) | undefined>(undefined)

  useEffect(() => {
    const unsubAndReturn = () => {
      setLoading(false)
      if (unsub.current) unsub.current()
      return () => {}
    }

    if (!collectionId || !collection) {
      // the collection went away — drop its items so they can't linger as stale data
      // (e.g. a practice-scoped collection resolving to null once context loads)
      setItems([])
      return unsubAndReturn()
    }
    if (!accessLevel) return unsubAndReturn()
    if (paused) return unsubAndReturn()
    const { access } = collection ?? {}

    if (access && (!accessLevel || !access.includes(accessLevel))) {
      return unsubAndReturn()
    }
    if (unsub.current) unsub.current()
    subscribedCollectionId.current = collectionId
    unsub.current = subscribeToCollection(fetchData, collection, accessLevel, {
      onData: data => {
        setItems(data)
      },
      onError: newError => {
        showError(newError)
        setError(newError)
      },
      onLoading: setLoading,
    })
    return unsub.current || (() => {})
  }, [collection, paused, showError, accessLevel, collectionId, fetchData])
  useEffect(
    () => () => {
      if (unsub.current) unsub.current()
    },
    [],
  )

  const itemsMap = useMemo(() => arrayToObject(items) ?? {}, [items])

  return useMemo(
    () => ({
      items,
      itemsMap,
      loading,
      error,
    }),
    [items, itemsMap, loading, error],
  )
}

export const getCollectionItems = <T extends AnyObject>(id: CollectionId): CollectionItem<T>[] => {
  const { [id]: collection } = useCollections.getState()
  return (
    collection?.items && Array.isArray(collection.items) ? collection.items : []
  ) as CollectionItem<T>[]
}
